import React from "react";
import { Button, Descriptions, Table, Tag, Typography } from "antd";

import {
  ATTRIBUTIONS,
  CATEGORIES,
  actionLabel,
  actorLabel,
  fieldLabel,
  formatDate,
  formatValue,
  targetLabel,
  viaLabel,
} from "./labels";

const { Text } = Typography;

// Détail d'une entrée du journal : auteur, source, cible, champs modifiés (avant / après),
// IP et navigateur, empreinte. Les valeurs personnelles ne sont jamais conservées.
export default function AuditEntryDetails({ entry, onShowOperation }) {
  const changes = entry.changes || {};
  const rows = (entry.fields || []).map((field) => {
    const change = changes[field] || {};
    return {
      key: field,
      field,
      before: change.changed ? null : formatValue(field, change.before),
      after: change.changed ? null : formatValue(field, change.after),
      masked: !!change.changed,
    };
  });
  const attribution = ATTRIBUTIONS[entry.source && entry.source.attribution] || ATTRIBUTIONS.none;
  const actor = entry.actor || {};

  return (
    <>
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Action">{actionLabel(entry.action)}</Descriptions.Item>
        <Descriptions.Item label="Catégorie">
          {(CATEGORIES[entry.category] || {}).label || entry.category}
        </Descriptions.Item>
        <Descriptions.Item label="Date de l'action">{formatDate(entry.at)}</Descriptions.Item>
        <Descriptions.Item label="Journalisée le">{formatDate(entry.logged_at)}</Descriptions.Item>
        <Descriptions.Item label="Auteur" span={2}>
          {actorLabel(actor)}
          {actor.delegation_id && (
            <Tag style={{ marginLeft: 8 }}>délégation {actor.delegation_id}</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Source">{viaLabel(entry.source)}</Descriptions.Item>
        <Descriptions.Item label="Attribution">
          <Tag color={attribution.color}>{attribution.label}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Cible" span={2}>
          {targetLabel(entry.target)}
          {entry.target && entry.target.path && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              {entry.target.path}
            </Text>
          )}
        </Descriptions.Item>
        {(entry.ip || entry.user_agent) && (
          <Descriptions.Item label="IP / navigateur" span={2}>
            {entry.ip || "—"}
            {entry.ip_forwarded && entry.ip_forwarded !== entry.ip
              ? ` (via ${entry.ip_forwarded})`
              : ""}
            <br />
            <Text type="secondary">{entry.user_agent || "—"}</Text>
          </Descriptions.Item>
        )}
        {entry.details && (
          <Descriptions.Item label="Détails" span={2}>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              {JSON.stringify(
                entry.details,
                (key, value) =>
                  value && value.seconds !== undefined && value.nanoseconds !== undefined
                    ? formatDate({ toDate: () => new Date(value.seconds * 1000) })
                    : value,
                2
              )}
            </pre>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Opération" span={2}>
          {entry.source && entry.source.op ? (
            <>
              <Text code>{entry.source.op}</Text>
              {onShowOperation && (
                <Button size="small" type="link" onClick={() => onShowOperation(entry.source.op)}>
                  Voir toute l'opération
                </Button>
              )}
            </>
          ) : (
            "—"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Empreinte" span={2}>
          <Text code copyable>
            {entry.hash}
          </Text>
        </Descriptions.Item>
      </Descriptions>

      {rows.length > 0 && (
        <Table
          style={{ marginTop: 16 }}
          size="small"
          pagination={false}
          dataSource={rows}
          columns={[
            { title: "Champ", dataIndex: "field", render: (field) => fieldLabel(field) },
            {
              title: "Avant",
              dataIndex: "before",
              render: (value, row) => (row.masked ? <Text type="secondary">—</Text> : value),
            },
            {
              title: "Après",
              dataIndex: "after",
              render: (value, row) =>
                row.masked ? (
                  <Text type="secondary">modifié (donnée personnelle non conservée)</Text>
                ) : (
                  value
                ),
            },
          ]}
        />
      )}
    </>
  );
}
